import { Controller } from "@hotwired/stimulus";

// Controls a generic selectable/sortable/filterable grid
// Expected markup:
// - <table data-controller="grid" data-action="filter-popover:apply->grid#applyFilter filter-popover:clear->grid#clearFilter">
// - <tbody data-grid-target="tbody"> ... rows ... </tbody>
// - Row <tr> should have dataset attributes used for sort/filter: data-name, data-domain, data-is-deleted, etc.
// - Row checkboxes: data-grid-target="rowCheckbox" data-action="change->grid#rowChanged"
// - Select-all checkbox: data-grid-target="selectAll" data-action="change->grid#toggleAll"
// - Selected count element: data-grid-target="selectedCount"
// - Sort buttons: data-action="click->grid#sort" data-grid-key-param="name|isDeleted|checked"
export default class extends Controller {
  static targets = ["tbody", "rowCheckbox", "selectAll", "selectedCount", "promoteButton", "promoteForm", "cloneButton", "cloneForm", "deployButton", "deployForm"];

  connect() {
    this.currentSort = { key: null, dir: "asc" };
    // Hydrate checked state directly from URL-provided selected_ids
    const selected = this._loadSelection();
    this.rowCheckboxTargets.forEach(cb => {
      const tr = cb.closest('tr');
      const id = tr?.dataset.id;
      if (id && selected.has(id)) cb.checked = true;
    });
    // Initialize sort state from URL so icons reflect server sort
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get('sort');
    const dir = (urlParams.get('dir') || 'asc').toLowerCase();
    if (key) this.currentSort = { key, dir };
    this.updateSelectedCount();
    this.updatePromoteButtonState();
    this.updateCloneButtonState();
    this.updateDeployButtonState();
    this.updateSortIcons();
    // Intercept all pagination link clicks at the document level
    this._onDocumentClick = (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const container = document.getElementById('td-pagination');
      if (!container || !container.contains(a)) return;
      // Only intercept primary button, unmodified clicks
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      try {
        const u = new URL(a.getAttribute('href'), window.location.origin);
        const csvNow = this._selectedIdsCsv();
        if (csvNow) u.searchParams.set('selected_ids', csvNow); else u.searchParams.delete('selected_ids');
        window.location.assign(`${u.pathname}?${u.searchParams.toString()}`);
      } catch {
        window.location.assign(a.getAttribute('href'));
      }
    };
    document.addEventListener('click', this._onDocumentClick, true);
  }

  disconnect() {
    // Cleanup listener
    if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick, true);
  }

  // Selection
  toggleAll() {
    const checked = this.hasSelectAllTarget ? this.selectAllTarget.checked : false;
    const selected = this._loadSelection();
    this.rowCheckboxTargets.forEach(cb => {
      if (cb.disabled) return;
      cb.checked = checked;
      const tr = cb.closest('tr');
      const id = tr?.dataset.id;
      if (!id) return;
      if (checked) selected.add(id); else selected.delete(id);
    });
    this._syncSelectedIdsToUrl(selected);
    this.updateSelectedCount();
    this.updatePromoteButtonState();
    this.updateCloneButtonState && this.updateCloneButtonState();
    this.updateDeployButtonState();
  }

  rowChanged(e) {
    const cb = e.currentTarget || e.target;
    const tr = cb?.closest('tr');
    const id = tr?.dataset.id;
    if (!id) return;
    const selected = this._loadSelection();
    if (cb.checked) selected.add(id); else selected.delete(id);
    this._syncSelectedIdsToUrl(selected);
    this.updateSelectedCount();
    this.updatePromoteButtonState();
    this.updateCloneButtonState();
    this.updateDeployButtonState();
  }

  updateSelectedCount() {
    // Count total selected across all pages from URL
    const selectedSet = this._loadSelection();
    const total = selectedSet.size;
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = total;
    } else {
      // Fallback: support count element outside the table controller
      const el = document.getElementById('td-selected-count');
      if (el) el.textContent = total;
    }
  }

  // Promote: add selected_ids param and submit
  beforeSubmitPromote(e) {
    // Ensure we include all selected IDs across pages
    const form = e.currentTarget || (this.hasPromoteFormTarget ? this.promoteFormTarget : null) || this.element.closest('form');
    if (!form) return;
    // Remove any previous hidden field
    form.querySelectorAll('input[name="selected_ids"]').forEach(el => el.remove());
    form.querySelectorAll('input[name="promotion[table_definition_ids][]"]').forEach(el => el.remove());
    // Merge selection from URL with currently checked checkboxes on this page
    const fromUrl = new Set(this._loadSelection());
    this.rowCheckboxTargets.forEach(cb => {
      if (cb.checked) {
        const tr = cb.closest('tr');
        const id = tr?.dataset?.id;
        if (id) fromUrl.add(String(id));
      }
    });
    // Build hyphen-delimited CSV of selected ids
    const csv = this._selectedIdsCsv(fromUrl);
    if (!csv) {
      e.preventDefault();
      return;
    }
    // selected_ids: keep for URL/state continuity
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'selected_ids';
    hidden.value = csv;
    form.appendChild(hidden);

    // Also provide the array expected by PromotionsController#create
    csv.split('-')
      .map(s => s.trim())
      .filter(s => /^\d+$/.test(s))
      .map(s => Number(s))
      .sort((a,b) => a - b)
      .forEach(id => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'promotion[table_definition_ids][]';
        input.value = String(id);
        form.appendChild(input);
      });
    // allow normal submit to continue
  }

  // Deploy: add selected_ids param and submit
  beforeSubmitDeploy(e) {
    if (e) e.preventDefault();
    const form = (e && e.currentTarget && e.currentTarget.closest('form')) || (this.hasDeployFormTarget ? this.deployFormTarget : null);
    if (!form) return;
    // Remove any previous hidden field
    form.querySelectorAll('input[name="selected_ids"]').forEach(el => el.remove());
    const csv = this._selectedIdsCsv();
    if (!csv) return; // nothing selected
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'selected_ids';
    hidden.value = csv;
    form.appendChild(hidden);
    // Submit the GET form
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      const evt = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(evt);
    }
  }

  // Sorting
  sort(e) {
    e.preventDefault();
    // Determine sort key from button
    const key = (e.params && e.params.key) || (e.currentTarget && e.currentTarget.dataset && e.currentTarget.dataset.gridKeyParam);
    if (!key) return;
    // Determine next dir from current URL
    const params = new URLSearchParams(window.location.search);
    const currentKey = params.get('sort');
    const curDirection = (params.get('dir') || 'asc').toLowerCase();
    const nextDir = (currentKey === key && curDirection === 'asc') ? 'desc' : 'asc';
    params.set('sort', key);
    params.set('dir', nextDir);
    // Carry selected_ids from URL
    const selectedIds = this._selectedIdsCsv();
    if (selectedIds) params.set('selected_ids', selectedIds); else params.delete('selected_ids');
    // Reset pagination when sort changes
    params.set('td_page', '1');
    const next = `${window.location.pathname}?${params.toString()}`;
    window.location.assign(next);
  }

  // Filtering (events from filter-popover controller)
  applyFilter(e) {
    const { field, value } = e.detail || {};
    if (!field) return;
    // Server-side filtering: update query params and reload
    const params = new URLSearchParams(window.location.search);
    if ((value || '').trim().length > 0) {
      params.set(field, String(value).trim());
    } else {
      params.delete(field);
    }
    // Reset pagination when filters change
    params.set('td_page', '1');
    // Carry selected_ids from URL
    const selectedIds = this._selectedIdsCsv();
    if (selectedIds) params.set('selected_ids', selectedIds); else params.delete('selected_ids');
    const next = `${window.location.pathname}?${params.toString()}`;
    window.location.assign(next);
  }

  clearFilter(e) {
    const { field } = e.detail || {};
    if (!field) return;
    const params = new URLSearchParams(window.location.search);
    params.delete(field);
    params.set('td_page', '1');
    // Carry selected_ids from URL
    const selectedIds = this._selectedIdsCsv();
    if (selectedIds) params.set('selected_ids', selectedIds); else params.delete('selected_ids');
    const next = `${window.location.pathname}?${params.toString()}`;
    window.location.assign(next);
  }

  // Selection persistence helpers
  _loadSelection() {
    const arr = this._parseSelectedIdsParam();
    return new Set(arr.map(String));
  }
  _selectedIdsCsv(selectedSet) {
    // Hyphen-delimited, unique, numeric-sort for stable URLs
    const ids = Array.from(selectedSet || this._loadSelection())
      .map(s => String(s).trim())
      .filter(s => /^\d+$/.test(s))
      .map(s => Number(s));
    const uniqueSorted = Array.from(new Set(ids)).sort((a, b) => a - b);
    return uniqueSorted.join('-');
  }
  _syncSelectedIdsToUrl(selectedSet) {
    const params = new URLSearchParams(window.location.search);
    const csv = this._selectedIdsCsv(selectedSet);
    if (csv) params.set('selected_ids', csv); else params.delete('selected_ids');
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', url);
  }
  // No pagination link rewriting; we inject selected_ids at click time only (Option A)
  _parseSelectedIdsParam() {
    const params = new URLSearchParams(window.location.search);
    const csv = params.get('selected_ids');
    if (!csv) return [];
    // Only hyphen '-' as delimiter; sanitize to digits only
    return csv
      .split('-')
      .map(s => s.trim())
      .filter(s => /^\d+$/.test(s));
  }
  // Toggle sort icons inside buttons according to currentSort
  updateSortIcons() {
    // Select all sort buttons by their declared sort key
    const buttons = this.element.querySelectorAll('[data-grid-key-param]');
    buttons.forEach(btn => {
      const key = btn.dataset.gridKeyParam;
      // Default to asc icon when not the active sort key (null or asc requirement)
      const dir = (this.currentSort?.key === key) ? this.currentSort.dir : 'asc';
      const up = btn.querySelector('[data-grid-sort-up]');
      const down = btn.querySelector('[data-grid-sort-down]');
      if (up) up.classList.toggle('hidden', dir === 'desc');
      if (down) down.classList.toggle('hidden', dir !== 'desc');
    });
  }

  // Enable the Promote button only if at least one checked row on the current page is not promoted
  updatePromoteButtonState() {
    if (!this.hasPromoteButtonTarget) return;
    // Determine if any currently checked row has data-promoted="false"
    let enabled = false;
    this.rowCheckboxTargets.forEach(cb => {
      if (cb.checked) {
        const tr = cb.closest('tr');
        const promoted = (tr?.dataset?.promoted || '').toString();
        if (promoted === 'false') enabled = true;
      }
    });
    const btn = this.promoteButtonTarget;
    btn.disabled = !enabled;
    btn.classList.toggle('opacity-50', !enabled);
    btn.classList.toggle('cursor-not-allowed', !enabled);
  }

  // Enable the Clone button when at least one row is selected across pages (from URL param)
  updateCloneButtonState() {
    if (!this.hasCloneButtonTarget) return;
    const totalSelected = this._loadSelection().size;
    const enabled = totalSelected > 0;
    const btn = this.cloneButtonTarget;
    btn.disabled = !enabled;
    btn.classList.toggle('opacity-50', !enabled);
    btn.classList.toggle('cursor-not-allowed', !enabled);
  }

  // Enable the Deploy button only if at least one checked row on the current page requires deployment
  updateDeployButtonState() {
    if (!this.hasDeployButtonTarget) return;
    let enabled = false;
    this.rowCheckboxTargets.forEach(cb => {
      if (cb.checked) {
        const tr = cb.closest('tr');
        const requiresDeployment = (tr?.dataset?.requiresDeployment || '').toString();
        if (requiresDeployment === 'true') enabled = true;
      }
    });
    const btn = this.deployButtonTarget;
    btn.disabled = !enabled;
    btn.classList.toggle('opacity-50', !enabled);
    btn.classList.toggle('cursor-not-allowed', !enabled);
  }

  // Clone: open bulk clone dialog by submitting GET with selected_ids into the turbo-frame
  openCloneDialog(e) {
    if (e) e.preventDefault();
    const form = (e && e.currentTarget && e.currentTarget.closest('form')) || (this.hasCloneFormTarget ? this.cloneFormTarget : null);
    if (!form) return;
    // Remove any previous hidden field
    form.querySelectorAll('input[name="selected_ids"]').forEach(el => el.remove());
    const csv = this._selectedIdsCsv();
    if (!csv) return; // nothing selected
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'selected_ids';
    hidden.value = csv;
    form.appendChild(hidden);
    // Submit the GET form (Turbo will target the frame specified on the form)
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      // Fallback for older browsers: dispatch a submit event so Turbo intercepts it
      const evt = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(evt);
    }
  }
}
